import React, { useState } from "react";
import { Variable, Plus, X, Trash2 } from "lucide-react";

/**
 * Variable Manager – sidebar panel showing all flow variables.
 *
 * @param {Array}    flowVariables    – [{ id, name, description }]
 * @param {Function} onAddVariable    – (name, description) => void
 * @param {Function} onRemoveVariable – (id) => void
 */
const VariableManager = ({ flowVariables = [], onAddVariable, onRemoveVariable }) => {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [error, setError] = useState("");

  const handleAdd = () => {
    const trimmed = newName.trim().toLowerCase().replace(/[^a-z0-9_]/g, "_");
    if (!trimmed) {
      setError("Variable name is required");
      return;
    }
    if (flowVariables.some((v) => v.name === trimmed)) {
      setError(`"${trimmed}" already exists`);
      return;
    }
    onAddVariable(trimmed, newDesc.trim());
    setNewName("");
    setNewDesc("");
    setAdding(false);
    setError("");
  };

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Variable size={16} className="text-[#008069]" />
          <h3 className="font-bold text-sm text-gray-900">Variables</h3>
          <span className="text-[10px] bg-[#E7FCE8] text-[#008069] px-1.5 py-0.5 rounded-full font-bold">
            {flowVariables.length}
          </span>
        </div>
        <button
          onClick={() => setAdding(!adding)}
          className="p-1.5 hover:bg-[#E7FCE8] rounded-lg transition-colors"
          title="Add variable"
        >
          <Plus size={16} className="text-[#008069]" />
        </button>
      </div>

      {/* Add form */}
      {adding && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 space-y-2">
          <input
            type="text"
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setError(""); }}
            placeholder="variable_name"
            className="w-full px-3 py-2 text-xs font-mono border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#008069] focus:outline-none"
            autoFocus
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
          />
          <input
            type="text"
            value={newDesc}
            onChange={(e) => setNewDesc(e.target.value)}
            placeholder="Description (optional)"
            className="w-full px-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-[#008069] focus:outline-none"
          />
          {error && <p className="text-[10px] text-red-600 font-medium">{error}</p>}
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              className="flex-1 py-1.5 text-[11px] font-bold bg-[#008069] text-white rounded-lg hover:bg-[#006e5a] transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => { setAdding(false); setNewName(""); setNewDesc(""); setError(""); }}
              className="flex-1 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Variable list */}
      <div className="max-h-[300px] overflow-y-auto">
        {flowVariables.length === 0 ? (
          <div className="px-4 py-6 text-center">
            <Variable size={24} className="text-gray-300 mx-auto mb-2" />
            <p className="text-[11px] text-[#8696a0] font-medium">No variables yet</p>
            <p className="text-[9px] text-[#8696a0] mt-0.5">
              Variables are created by AI Ask Question nodes or manually here.
            </p>
          </div>
        ) : (
          flowVariables.map((v) => (
            <div
              key={v.id || v.name}
              className="px-4 py-2.5 flex items-center justify-between border-b border-gray-100 last:border-0 hover:bg-gray-50 group"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-mono font-bold text-[#008069]">{`{{${v.name}}}`}</span>
                </div>
                {v.description && (
                  <p className="text-[9px] text-[#8696a0] mt-0.5 truncate">{v.description}</p>
                )}
              </div>
              {!v.isSystem && (
                <button
                  onClick={() => onRemoveVariable(v.id || v.name)}
                  className="p-1 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all rounded"
                  title="Remove variable"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default VariableManager;
